import { UpgradeModel } from "types";

import castleCoreImage from "assets/upgrade-castle-core.png";
import energyBeamImage from "assets/upgrade-energy-beam.svg";
import energyScopeImage from "assets/upgrade-energy-scope.svg";
import flashNexusImage from "assets/upgrade-flash-nexus.png";
import forceNexusImage from "assets/upgrade-force-nexus.png";
import fortressCoreImage from "assets/upgrade-fortress-core.png";
import hyperReactorImage from "assets/upgrade-hyper-reactor.png";
import hypernovaImage from "assets/upgrade-hypernova.png";
import lockCoreImage from "assets/upgrade-lock-core.png";
import megaReactorImage from "assets/upgrade-mega-reactor.png";
import moonNexusImage from "assets/upgrade-moon-nexus.png";
import nexusRocketImage from "assets/upgrade-nexus-rocket.png";
import sharpCoreImage from "assets/upgrade-sharp-core.png";
import starNexusImage from "assets/upgrade-star-nexus.png";
import strikerNexusImage from "assets/upgrade-striker-nexus.png";
import supernovaImage from "assets/upgrade-supernova.png";
import swiftNexusImage from "assets/upgrade-swift-nexus.png";
import vaporCoreImage from "assets/upgrade-vapor-core.png";
import voidCutterImage from "assets/upgrade-void-cutter.svg";
import voidGuardImage from "assets/upgrade-void-guard.png";
import voidScopeImage from "assets/upgrade-void-scope.svg";
import voidWallImage from "assets/upgrade-void-wall.png";
import voidburnerImage from "assets/upgrade-voidburner.png";
import zephyrCoreImage from "assets/upgrade-zephyr-core.png";

import {
  attackAnimation,
  reactorCoreAnimation,
  shieldsAnimation,
  targetingSystemAnimation,
  thrusterAnimation,
} from "components/styles/animations";
import {
  DEFAULT_REACTOR_SPEED,
  DEFAULT_TARGETING_SPEED,
  DEFAULT_THRUSTERS_SPEED,
  DEFAULT_WEAPONS_SPEED,
} from "../constants";

const SENECA_HAVEN_UPGRADES: Record<string, UpgradeModel> = {
  // WEAPONS
  energy_beam: {
    slug: "energy_beam",
    name: "Energy Beam",
    description:
      "An antimatter laser that weakens ship parts and increases your pilot skill power.",
    image: energyBeamImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 6,
      energy: 3,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxWeakenParts: 1,
      energyMultiplier: 1,
    },
  },
  void_cutter: {
    slug: "void_cutter",
    name: "Void Cutter",
    description:
      "A powerful antimatter laser that increases your max damage and also your pilot skill power.",
    image: voidCutterImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 7,
      energy: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 1,
      energyMultiplier: 1,
    },
  },
  // SHIELDS
  void_wall: {
    slug: "void_wall",
    name: "Void Wall",
    description:
      "An antimatter force field that deflects damage and recharges your pilot skills faster.",
    image: voidWallImage,
    animation: shieldsAnimation,
    animationSpeed: 600,
    part: "shields",
    requirements: {
      resilience: 4,
      energy: 2,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      damageReduction: 1,
      fasterRecharge: 1,
    },
  },
  void_guard: {
    slug: "void_guard",
    name: "Void Guard",
    description:
      "A powerful antimatter force field increases your ship's health as well as your pilot skill power.",
    image: voidGuardImage,
    animation: shieldsAnimation,
    animationSpeed: 600,
    part: "shields",
    requirements: {
      resilience: 10,
      energy: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 1,
      energyMultiplier: 1,
    },
  },
  // THRUSTERS
  nexus_rocket: {
    slug: "nexus_rocket",
    name: "Nexus Rocket",
    description:
      "Special energy thrusters that elevate your ability to avoid attacks and increases your pilot skill power.",
    image: nexusRocketImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 8,
      energy: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackEvasion: 1,
      energyMultiplier: 1,
    },
  },
  voidburner: {
    slug: "voidburner",
    name: "Voidburner",
    description:
      "Powerful energy thrusters that elevate the frequency of your attacks as well as recharges your pilot skills faster. Speed on speed.",
    image: voidburnerImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 8,
      energy: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 1,
      fasterRecharge: 1,
    },
  },
  // TARGETING SYSTEMS
  energy_scope: {
    slug: "energy_scope",
    name: "Energy Scope",
    description:
      "An antimatter-based targeting system upgrade that increases your minimum damage and recharges your skills faster.",
    image: energyScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 5,
      energy: 3,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      minAttackDamage: 1,
      fasterRecharge: 1,
    },
  },
  void_scope: {
    slug: "void_scope",
    name: "Void Scope",
    description:
      "An antimatter-based targeting system upgrade that helps you land attacks more consistently and increases your pilot skill power.",
    image: voidScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 6,
      energy: 3,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 1,
      energyMultiplier: 1,
    },
  },
  // REACTOR CORES
  vapor_core: {
    slug: "vapor_core",
    name: "Vapor Core",
    description:
      "An upgrade to your reactor core that boosts your skill recharging speed and power.",
    image: vaporCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 8,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.75,
      fasterRecharge: 0.25,
    },
  },
  zephyr_core: {
    slug: "zephyr_core",
    name: "Zephyr Core",
    description:
      "An solid upgrade to your reactor core that boosts your skill recharging speed and power.",
    image: zephyrCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 10,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.25,
      fasterRecharge: 0.75,
    },
  },
  mega_reactor: {
    slug: "mega_reactor",
    name: "Mega Reactor",
    description:
      "A decently powerful upgrade to your reactor core that boosts your skill recharging speed and power.",
    image: megaReactorImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 12,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  hyper_reactor: {
    slug: "hyper_reactor",
    name: "Hyper Reactor",
    description:
      "A quite powerful upgrade to your reactor core that really boosts your skill power.",
    image: hyperReactorImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 15,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
    },
  },
  sharp_core: {
    slug: "sharp_core",
    name: "Sharp Core",
    description:
      "A powerful upgrade to your reactor core that boosts your skill power and min attack damage.",
    image: sharpCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 9,
      precision: 5,
    },
    grade: "elite",
    derivedStatsModifiers: {
      fasterRecharge: 1,
      minAttackDamage: 1,
    },
  },
  lock_core: {
    slug: "lock_core",
    name: "Lock Core",
    description:
      "A powerful upgrade to your reactor core that boosts your skill recharging speed and attack accuracy.",
    image: lockCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 9,
      precision: 5,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      attackAccuracy: 1,
    },
  },
  force_nexus: {
    slug: "force_nexus",
    name: "Force Nexus",
    description:
      "A powerful upgrade to your reactor core that boosts your skill power and ability to weaken opponent parts.",
    image: forceNexusImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 11,
      firepower: 6,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      maxWeakenParts: 1,
    },
  },
  striker_nexus: {
    slug: "striker_nexus",
    name: "Striker Nexus",
    description:
      "A powerful upgrade to your reactor core that boosts your skill recharging speed and max attack damage.",
    image: strikerNexusImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 12,
      firepower: 6,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      fasterRecharge: 1,
      maxAttackDamage: 1,
    },
  },
  swift_nexus: {
    slug: "swift_nexus",
    name: "Swift Nexus",
    description:
      "A powerful upgrade to your reactor core that boosts your skill power and evasion speed.",
    image: swiftNexusImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 13,
      speed: 7,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      attackEvasion: 1,
    },
  },
  flash_nexus: {
    slug: "flash_nexus",
    name: "Flash Nexus",
    description:
      "A powerful upgrade to your reactor core that boosts your skill recharging speed and attack speed.",
    image: flashNexusImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 14,
      speed: 7,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      fasterRecharge: 1,
      attackSpeed: 1,
    },
  },
  castle_core: {
    slug: "castle_core",
    name: "Castle Core",
    description:
      "A powerful upgrade to your reactor core that boosts your pilot skill power and ship parts protection.",
    image: castleCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 17,
      resilience: 9,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 1,
      weakenPartsReduction: 1,
    },
  },
  fortress_core: {
    slug: "fortress_core",
    name: "Fortress Core",
    description:
      "An extremely powerful upgrade to your reactor core that boosts your skill recharging speed and also shields somehow.",
    image: fortressCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 18,
      resilience: 9,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      fasterRecharge: 1,
      damageReduction: 1,
    },
  },
  moon_nexus: {
    slug: "moon_nexus",
    name: "Moon Nexus",
    description:
      "A powerful upgrade to your reactor core that boosts the speed and power of your abilities.",
    image: moonNexusImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 23,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.25,
      fasterRecharge: 0.75,
    },
  },
  star_nexus: {
    slug: "star_nexus",
    name: "Star Nexus",
    description:
      "A very powerful upgrade to your reactor core that boosts the power and speed of your abilities.",
    image: starNexusImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 25,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.75,
      fasterRecharge: 0.25,
    },
  },
  supernova: {
    slug: "supernova",
    name: "Surge Nova",
    description:
      "Install this core at your own risk. Or really, the risk of your opponents.",
    image: supernovaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 27,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  hypernova: {
    slug: "hypernova",
    name: "Force Nova",
    description: "This thing looks intense. I'd be careful if I were you.",
    image: hypernovaImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 30,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      energyMultiplier: 1,
    },
  },
};

export default SENECA_HAVEN_UPGRADES;
